import { useEffect, useState } from 'react';
import { studioClients } from '@/lib/api';

interface Client {
  id: string;
  client_name: string;
  client_email?: string | null;
  status: string;
  project_ids: string[];
  invited_at?: string | null;
  activated_at?: string | null;
  billing_active: boolean;
}

export function ClientTenants() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadClients() {
    try {
      const data = await studioClients.list();
      setClients((data.clients ?? []) as Client[]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadClients(); }, []);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteName.trim()) return;
    setInviting(true);
    setError(null);
    try {
      await studioClients.invite({ client_name: inviteName.trim(), client_email: inviteEmail.trim() || undefined });
      setInviteName('');
      setInviteEmail('');
      setShowInvite(false);
      await loadClients();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite client');
    } finally {
      setInviting(false);
    }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Client Tenants</h1>
          <p className="text-muted-foreground mt-1">Manage your clients' isolated workspaces</p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium"
        >
          + Invite Client
        </button>
      </div>

      {showInvite && (
        <div className="border border-border rounded-xl p-5 bg-card mb-6">
          <h2 className="text-sm font-semibold text-foreground mb-4">Invite a New Client</h2>
          <form onSubmit={(e) => void handleInvite(e)} className="space-y-3">
            <input
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Company / client name"
              required
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
            />
            <input
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Contact email (optional)"
              type="email"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={inviting}
                className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm disabled:opacity-50"
              >
                {inviting ? 'Inviting…' : 'Send Invite'}
              </button>
              <button type="button" onClick={() => setShowInvite(false)} className="border border-border px-4 py-2 rounded-lg text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />)}
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <p className="text-muted-foreground">No clients yet. Invite your first client to get started.</p>
        </div>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Apps</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Billing</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Invited</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{client.client_name}</p>
                    {client.client_email && <p className="text-xs text-muted-foreground">{client.client_email}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${client.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{client.project_ids.length}</td>
                  <td className="px-4 py-3">
                    <span className={client.billing_active ? 'text-green-600' : 'text-muted-foreground'}>
                      {client.billing_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {client.invited_at ? new Date(client.invited_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
